/**
 * Component adapted from ClickToCopy from "@blueprintjs/docs-app"
 */

import { HTMLDivProps, IProps, Keys, removeNonHTMLProps, Utils } from "@blueprintjs/core";
import classNames from "classnames";
import * as React from "react";

import styles from "./ClickToCopy.module.css";

export interface ClickToCopyProps extends IProps, HTMLDivProps {
    /** Value to copy when clicked */
    value: string;
}

export interface ClickToCopyState {
    hasCopied?: boolean;
}

/**
 * A handy little component that copies a given value to the clipboard when the user clicks it.
 * Provide a child element `.clipboardMessage` (from the accompanying CSS module);
 * the message will be rendered in an `::after` pseudoelement and will automatically change
 * on hover and after user has copied it.
 *
 * Add the following `data-` attributes to that child element to customize the message:
 *  - `[data-message="<message>"]` will be shown by default, when the element is not interacted with.
 *  - `[data-hover-message="<message>"]` will be shown when the element is hovered.
 *  - `[data-copied-message="<message>"]` will be shown when the element has been copied.
 *
 * The message is reset to default when the user mouses off the element after copying it.
 */
export default class extends React.PureComponent<ClickToCopyProps, ClickToCopyState> {
    public readonly displayName = "ClickToCopy";
    public static readonly defaultProps: ClickToCopyProps = {
        value: "",
    };

    public state: ClickToCopyState = {
        hasCopied: false,
    };

    private inputElement?: HTMLInputElement | null;

    private refHandlers = {
        input: (input: HTMLInputElement) => (this.inputElement = input),
    };

    public render() {
        const { className, children, value } = this.props;
        return (
            <div
                {...removeNonHTMLProps(this.props, ["value"], true)}
                className={classNames(styles.container, className, {
                    [styles.clipboardCopied]: this.state.hasCopied,
                })}
                onClick={this.handleClickEvent}
                onMouseLeave={this.handleMouseLeave}
                tabIndex={0}
            >
                <input
                    className={styles.input}
                    onBlur={this.handleMouseLeave as any}
                    onKeyDown={this.handleKeyDown}
                    readOnly={true}
                    ref={this.refHandlers.input}
                    value={value}
                />
                {children}
            </div>
        );
    }

    private handleClickEvent = (e: React.MouseEvent<HTMLDivElement>) => {
        this.inputElement?.select();
        document.execCommand("copy");
        this.setState({ hasCopied: true });
        Utils.safeInvoke(this.props.onClick, e);
    };

    // tslint:disable-next-line:member-ordering
    private handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.keyCode === Keys.SPACE || e.keyCode === Keys.ENTER) {
            this.handleClickEvent(e as any);
        }
    };

    private handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
        this.setState({ hasCopied: false });
        Utils.safeInvoke(this.props.onMouseLeave, e);
    };
}
